/* istanbul ignore file */
import React, { useState, ChangeEvent, useEffect } from 'react'
import { Button, Chip, Icon } from '../../elementals'
import { ChipColor } from '../../elementals/chip'
import InputField from '../inputField'
import { useApplicationTheme } from '../../applicationTheme'
import { getThemedCompact } from '../util'
import styles from './styles.scss'
import classNames from 'classnames/bind'

const cx = classNames.bind(styles)

export interface EditableListInputFieldProps {
  isValidValue: (value: string) => boolean
  invalidValueErrorMessage: string
  helpContent?: React.ReactNode
  initialValues?: string[]
  elmId: string
  onChange: (items: string[]) => void
  label: string
  compact?: boolean
}

const EditableListInputField = ({
  isValidValue,
  invalidValueErrorMessage,
  helpContent,
  onChange: onChangeProp,
  initialValues,
  elmId,
  label,
  compact,
  ...rest
}: EditableListInputFieldProps) => {
  const theme = useApplicationTheme()
  const newCompact = getThemedCompact(theme, compact)
  const [items, setItems] = useState<string[]>(
    Array.isArray(initialValues) ? initialValues : []
  )
  const [inputValue, setInputValue] = useState<string>('')
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (initialValues && initialValues !== items) {
      setItems(initialValues)
    }
  }, [initialValues])

  const onAddItem = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (!isValidValue(inputValue)) {
      setError(invalidValueErrorMessage)
      return
    }
    const newItems = [...items, inputValue]
    setItems(newItems)
    setInputValue('')
    onChangeProp(newItems)
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    event.stopPropagation()
    setError(undefined)
    setInputValue(event.target?.value)
  }

  const removeItemFromList = (itemToRemove: string) => {
    const newItems = items.filter(item => item !== itemToRemove)
    setItems(newItems)
    onChangeProp(newItems)
  }
  console.log('theme', theme, 'compact', compact, 'newCompact', newCompact) // eslint-disable-line

  return (
    <div>
      <label className={cx('list-label')}>{label}</label>
      {items?.length > 0 && (
        <div
          data-elm-id={`${elmId}_value-list`}
          className={cx('result-pill-container')}
        >
          {items.map((item, index) => (
            <Chip
              key={`${item}_${index}`}
              type='input'
              isSelected
              onClick={() => removeItemFromList(item)}
              content={item}
              color={ChipColor.Gray}
              selectionStyle='light'
              size='medium'
              elmId={`${elmId}_value-${index}`}
            />
          ))}
        </div>
      )}
      <div className={cx('input-container')}>
        <div style={{ width: '100%' }}>
          <InputField
            elmId={`${elmId}_input`}
            helpContent={helpContent}
            onChange={onChange}
            value={inputValue}
            {...rest}
            compact={newCompact}
            error={error}
            touched
            label='Enter new value'
          />
        </div>
        <div className={cx('button-container')}>
          <Button
            role='secondary'
            onClick={onAddItem}
            icon='right'
            elmId={`${elmId}_add-button`}
            disabled={!(inputValue?.length > 0)}
          >
            Add
            <Icon
              name='plus'
              iconStyle='solid'
              color='inherit'
              style={{ marginLeft: '12px' }}
            />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default EditableListInputField
